import { Box, Button, Checkbox, Flex, Select } from '@gmg/gmg-react-components';
import { FC, useEffect } from 'react';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import countryList from 'react-select-country-list';
import styled from 'styled-components';
import FormElement from '../FormElement';
import FormElementWrapper from '../SelectElementWrapper';

interface FormModel {
    id: string;
    name: string;
    address: string;
    zip: string;
    city: string;
    country: { value: string; label: string };
    contactPerson: string;
    contactEmail: string;
    contactPhone: string;
    isAllowedToUseLicenseSystemsOffline: boolean;
}

export interface SubmitDataModel extends Omit<FormModel, 'country'> { country: string }
export interface NewOrgModalProps {
    onClose?: () => void;
    onSubmit: SubmitHandler<SubmitDataModel>;
    existingOrgNames: string[] | undefined;
    toBeEditedOrganization?: SubmitDataModel;
    showIsAllowedToUseLicenseSystemsOfflineCheckBox: boolean;
}

const AddOrEditOrgModal: FC<NewOrgModalProps> = props => {
    const { t } = useTranslation();
    const useCase = !!props.toBeEditedOrganization ? 'edit' : 'new';

    const { register, handleSubmit, formState: { errors }, control, setValue } = useForm<FormModel>({ mode: 'onBlur' });

    const countryCodesOptions: Array<{ label: string; value: string }> = countryList().getData();

    const defaultRegisterOptionsForRequiredItems =
    {
        required: t('Validation.forms.field_required_val', 'Uh oh, please fill out this field.'),
        maxLength: {
            value: 50,
            message: t('Validation.forms.maxLength_exceeded_val', {
                defaultValue: 'Oops, you have exceeded the limit of {{maxCharacters}} characters.',
                maxCharacters: 50,
            }),
        },
        setValueAs: (value: string) => value.trim(),
    };
    const defaultRegisterOptionsForOptionalItems =
    {
        required: false,
        maxLength: {
            value: 50,
            message: t('Validation.forms.maxLength_exceeded_val', {
                defaultValue: 'Oops, you have exceeded the limit of {{maxCharacters}} characters.',
                maxCharacters: 50,
            }),
        },
        setValueAs: (value: string) => value.trim(),
    };
    const registerOptionsForName =
    {
        required: t('Validation.forms.field_required_val', 'Please fill out this field.'),
        maxLength: {
            value: 50,
            message: t('Validation.forms.maxLength_exceeded_val', {
                defaultValue: 'Oops, you have exceeded the limit of {{maxCharacters}} characters.',
                maxCharacters: 50,
            }),
        },
        validate: {
            orgIsUnique: (value: string) => !props.existingOrgNames?.includes(value.toLowerCase())
                || t('NewOrgModal.input_name_duplicate_val', 'An organization with this name already exists. Please use a different name.'),
            orgNameContainsForbiddenCharacters: (value: string) => {
                // We support only Latin characters in organization names, to avoid problems with the invitation email / Join Organization page.
                // We could support it if we wanted to, but it would require more work.
                // eslint-disable-next-line no-control-regex
                const latin1Regex = /^[\u0000-\u00FF]*$/;
                return latin1Regex.test(value)
                    ? true
                    : t('NewOrgModal.input_name_contains_forbidden_characters_val', 'Special characters like \'č\', \'š\', or \'ž\' are not supported. Please use only standard Latin letters (a-z) for organisation names.');
            },
        },
        setValueAs: (value: string) => value.trim(),
    };

    const emailRegex = /^[\w!#$%&'*+/=?`{|}~^-]+(?:\.[\w!#$%&'*+/=?`{|}~^-]+)*@(?:[a-zA-ZäöüÄÖÜ0-9-]+\.)+[a-zA-Z]{2,}$/;

    const registerOptionsForEmail =
    {
        required: t('Validation.forms.field_required_val', 'Please fill out this field.'),
        maxLength: {
            value: 50,
            message: t('Validation.forms.maxLength_exceeded_val', {
                defaultValue: 'Oops, you have exceeded the limit of {{maxCharacters}} characters.',
                maxCharacters: 50,
            }),
        },
        validate: {
            isValid: (value: string) => emailRegex.test(value.trim())
                ? true
                : t('Validation.forms.invalidEmail_val', 'It seems like your email address is invalid. Please try again.'),
        },
        setValueAs: (value: string) => value.trim(),
    };

    useEffect(() => {
        // set initial value for the modal if any in EDIT useCase
        const preSelectedCountry = countryCodesOptions.find(c => c.value === props.toBeEditedOrganization?.country);
        if (useCase === 'new' || !preSelectedCountry) return;

        setValue('country', preSelectedCountry);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const getDefaultValueForProp = (propName: keyof Omit<SubmitDataModel, 'isAllowedToUseLicenseSystemsOffline'>) => {
        if (!props.toBeEditedOrganization?.[propName] || useCase === 'new') return;

        return { defaultValue: props.toBeEditedOrganization?.[propName] };
    };

    return (
        <form onSubmit={handleSubmit(data => {
            props.onSubmit({
                ...data,
                country: data.country.value,
                // isAllowedToUseLicenseSystemsOffline can be missing in the data because most customers are not allowed to see the checkbox
                // In this case, it is important that the frontend does not change the current value of this attribute.
                // "undefined" will be handled respectively in the backend.
                isAllowedToUseLicenseSystemsOffline: data.isAllowedToUseLicenseSystemsOffline,
            });
        })}>
            <Flex
                flexDirection="row"
                flexWrap="wrap"
                width="100%"
                pb='20px'
            >
                <FormElement
                    dataProperty="name"
                    registerElement={{ ...register('name', registerOptionsForName) }}
                    validationError={errors?.name?.message}
                    label={t('NewOrgModal.input_name_tb', 'Organization name')}
                    placeholder={t('NewOrgModal.input_name_desc', 'Enter name of organization')}
                    {...getDefaultValueForProp('name')}
                />

                <FormElement
                    dataProperty="address"
                    registerElement={{ ...register('address', defaultRegisterOptionsForRequiredItems) }}
                    validationError={errors?.address?.message}
                    label={t('NewOrgModal.input_address_tb', 'Street name and number')}
                    placeholder={t('NewOrgModal.input_address_desc', 'Enter the street name and number')}
                    {...getDefaultValueForProp('address')}
                />

                <FormElement
                    dataProperty="city"
                    registerElement={{ ...register('city', defaultRegisterOptionsForRequiredItems) }}
                    validationError={errors?.city?.message}
                    variant="smallLeft"
                    label={t('NewOrgModal.input_city_tb', 'City')}
                    placeholder={t('NewOrgModal.input_city_desc', 'Enter city')}
                    {...getDefaultValueForProp('city')}
                />
                <FormElement
                    dataProperty="zip"
                    registerElement={{ ...register('zip', defaultRegisterOptionsForRequiredItems) }}
                    validationError={errors?.zip?.message}
                    variant="smallRight"
                    label={t('NewOrgModal.input_zip_tb', 'ZIP/postal code')}
                    placeholder={t('NewOrgModal.input_zip_desc', 'Enter ZIP/postal code')}
                    {...getDefaultValueForProp('zip')}
                />

                <Controller
                    control={control}
                    name="country"
                    rules={{ required: true }}
                    render={({ field: { onChange, ref, value } }) => (
                        <StyledWrapper
                            label={t('NewOrgModal.input_country_tb', 'Country')}
                            height="35px"
                            variant='small'
                        >
                            <Select
                                onChange={onChange}
                                ref={ref}
                                value={value}
                                options={countryCodesOptions}
                                placeholder={t('NewOrgModal.input_country_desc', 'Select country')}
                                name={'Role Controller'}
                                width="100%"
                                maxMenuHeight={200}
                                error={errors.country && t('Validation.forms.field_required_val', 'Uh oh, please fill out this field.')}
                            />
                        </StyledWrapper>
                    )}
                />
                <FormElement
                    dataProperty="contactPhone"
                    registerElement={{ ...register('contactPhone', defaultRegisterOptionsForOptionalItems) }}
                    validationError={errors?.contactPhone?.message}
                    variant="smallRight"
                    label={t('NewOrgModal.input_contactPhone_tb', 'Phone')}
                    placeholder={t('NewOrgModal.input_contactPhone_desc', 'Enter phone number')}
                    {...getDefaultValueForProp('contactPhone')}
                />
                <FormElement
                    dataProperty="contactPerson"
                    registerElement={{ ...register('contactPerson', defaultRegisterOptionsForOptionalItems) }}
                    validationError={errors?.contactPerson?.message}
                    label={t('NewOrgModal.input_contactPerson_tb', 'Contact name')}
                    placeholder={t('NewOrgModal.input_contactPerson_desc', 'Enter name of contact person')}
                    {...getDefaultValueForProp('contactPerson')}
                />
                <FormElement
                    dataProperty="contactEmail"
                    registerElement={{
                        ...register('contactEmail', registerOptionsForEmail),
                    }}
                    validationError={errors?.contactEmail?.message}
                    label={t('NewOrgModal.input_contactEmail_tb', 'Contact email adress')}
                    placeholder={t('NewOrgModal.input_contactEmail_desc', 'Enter contact email')}
                    {...getDefaultValueForProp('contactEmail')}
                />
                {props.showIsAllowedToUseLicenseSystemsOfflineCheckBox && <Box pl='24px'>
                    <Checkbox
                        {...register('isAllowedToUseLicenseSystemsOffline')}
                        aria-label='Offline license enabler checkbox'
                        id={'offline-licensing'}
                        label={t('NewOrgModal.enable_offline_licensing_cb', 'Approve offline Licensing (high security)')}
                        defaultChecked={!!props.toBeEditedOrganization?.isAllowedToUseLicenseSystemsOffline}
                    />
                </Box>
                }
            </Flex>

            <ButtonContainer>
                <Button
                    variant="primary"
                    type="submit"
                >
                    {useCase === 'new'
                        ? t('NewOrgModal.action_add_btn', 'Add')
                        : t('ButtonTitles.action_apply_btn', 'Apply')
                    }
                </Button>
                <Button
                    variant="secondary"
                    type="reset"
                    onClick={props.onClose}
                >
                    {t('NewOrgModal.action_cancel_btn', 'Cancel')}
                </Button>
            </ButtonContainer>
        </form>
    );
};

const ButtonContainer = styled.div`
    box-sizing: border-box;
    display: flex;
    flex-direction: row;
    justify-content: flex-end;
    width: 100%;
    padding-right: 23px;
    & button {
        margin-left: 10px;
    }
    gap: 8px;
`;

const StyledWrapper = styled(FormElementWrapper)`
    padding-right: 16px;

    //TODO: a kind of quick hack for
    //this component only, fix when ui restyles the modals
    border-top: 2px solid ${({ theme }) => theme.colors.backgroundPaper};
    margin-top: -1px;
`;

export default AddOrEditOrgModal;